import { Award, Sparkles, Lock, Check, Video, Coins } from "lucide-react";

/**
 * MilestoneLadder — inline progress bar showing the user's advancement
 * through the referral tiers, unlocked badges, and any available redemption
 * codes (e.g. free attorney consult perk).
 *
 * Fed the same `summary` object returned by GET /api/referrals/summary.
 */
export default function MilestoneLadder({ summary }) {
  if (!summary || !summary.milestones?.length) return null;

  const earned = summary.earned_count || 0;
  const highest = summary.milestones[summary.milestones.length - 1].at_earned_count;
  const progressPct = Math.min(100, Math.round((earned / highest) * 100));

  return (
    <div
      className="rounded-xl p-4 sm:p-5 border"
      style={{
        borderColor: "var(--ai-border)",
        background: "rgba(255, 255, 255, 0.7)",
      }}
      data-testid="milestone-ladder"
    >
      <div className="flex items-center gap-2 mb-3">
        <Award className="w-4 h-4" style={{ color: "var(--action)" }} />
        <div
          className="font-display font-bold text-sm"
          style={{ color: "var(--text-primary)" }}
        >
          Milestone rewards
        </div>
        <div
          className="text-[11px] ml-auto"
          style={{ color: "var(--text-secondary)" }}
          data-testid="milestone-progress-label"
        >
          {earned} of {highest} paid
        </div>
      </div>

      {/* Progress bar with tier markers */}
      <div
        className="relative h-2 rounded-full mb-4"
        style={{ background: "var(--border-stone)" }}
      >
        <div
          className="absolute inset-y-0 left-0 rounded-full transition-all"
          style={{ width: `${progressPct}%`, background: "var(--action)" }}
        />
        {summary.milestones.map((m) => {
          const leftPct = Math.round((m.at_earned_count / highest) * 100);
          const hit = earned >= m.at_earned_count;
          return (
            <div
              key={m.key}
              className="absolute -top-1 -translate-x-1/2 w-4 h-4 rounded-full border-2 flex items-center justify-center"
              style={{
                left: `${leftPct}%`,
                background: hit ? "var(--action)" : "#fff",
                borderColor: hit ? "var(--action)" : "var(--border-stone)",
              }}
              title={`${m.label} at ${m.at_earned_count} referrals`}
            >
              {hit && <Check className="w-2.5 h-2.5 text-white" strokeWidth={4} />}
            </div>
          );
        })}
      </div>

      {/* Tier cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
        {summary.milestones.map((m) => {
          const unlocked = !!m.unlocked;
          const isNext = !unlocked && summary.next_milestone?.key === m.key;
          return (
            <div
              key={m.key}
              data-testid={`milestone-tier-${m.key}`}
              className="rounded-lg p-3 border"
              style={{
                borderColor: unlocked
                  ? "var(--action)"
                  : isNext
                  ? "var(--ai-border)"
                  : "var(--border-stone)",
                background: unlocked
                  ? "linear-gradient(135deg, #FFF6EF 0%, #FFFFFF 100%)"
                  : "#FFFFFF",
                opacity: unlocked || isNext ? 1 : 0.65,
              }}
            >
              <div className="flex items-start gap-2 mb-1">
                <div
                  className="w-6 h-6 rounded-full flex items-center justify-center shrink-0 mt-0.5"
                  style={{
                    background: unlocked
                      ? "var(--action)"
                      : "var(--border-stone)",
                  }}
                >
                  {unlocked ? (
                    <Sparkles className="w-3 h-3 text-white" />
                  ) : (
                    <Lock className="w-3 h-3" style={{ color: "var(--text-secondary)" }} />
                  )}
                </div>
                <div className="min-w-0">
                  <div
                    className="font-semibold text-xs sm:text-sm flex items-center gap-1.5 flex-wrap"
                    style={{ color: "var(--text-primary)" }}
                  >
                    {m.label}
                    {unlocked && (
                      <span
                        className="pill pill-ok text-[10px]"
                        data-testid={`milestone-badge-${m.key}`}
                      >
                        <Check className="w-2.5 h-2.5" /> Unlocked
                      </span>
                    )}
                  </div>
                  <div
                    className="text-[11px] leading-snug mt-0.5"
                    style={{ color: "var(--text-secondary)" }}
                  >
                    {m.description}
                  </div>
                  <div
                    className="text-[10px] mt-1 flex items-center gap-1"
                    style={{ color: "var(--action)" }}
                  >
                    {m.bonus_credit_cents > 0 && (
                      <span className="flex items-center gap-0.5">
                        <Coins className="w-3 h-3" /> ${m.bonus_credit_cents / 100}
                      </span>
                    )}
                    {m.perk_kind === "free_consult" && (
                      <span className="flex items-center gap-0.5">
                        <Video className="w-3 h-3" /> Free attorney call
                      </span>
                    )}
                    <span className="ml-1" style={{ color: "var(--text-secondary)" }}>
                      · at {m.at_earned_count} referrals
                    </span>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Available perks (redemption codes) */}
      {summary.perks?.length > 0 && (
        <div
          className="mt-3 pt-3 border-t"
          style={{ borderColor: "var(--border-stone)" }}
          data-testid="milestone-perks"
        >
          {summary.perks.map((p) => (
            <div
              key={p.id}
              className="rounded-lg p-3 flex items-start gap-2.5"
              style={{ background: "var(--ai-bg)", border: "1px solid var(--ai-border)" }}
              data-testid={`perk-${p.kind}`}
            >
              <Video className="w-4 h-4 mt-0.5 shrink-0" style={{ color: "var(--ai-text)" }} />
              <div className="min-w-0 flex-1">
                <div
                  className="font-semibold text-xs sm:text-sm"
                  style={{ color: "var(--ai-text)" }}
                >
                  {p.title}
                </div>
                <div
                  className="text-[11px] mt-0.5"
                  style={{ color: "var(--text-secondary)" }}
                >
                  {p.note}
                </div>
                <div className="mt-2 flex items-center gap-2">
                  <span
                    className="font-mono text-xs px-2 py-1 rounded"
                    style={{
                      background: "#fff",
                      border: "1px dashed var(--ai-border)",
                      color: "var(--action)",
                    }}
                    data-testid={`perk-code-${p.kind}`}
                  >
                    {p.code}
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      navigator.clipboard?.writeText(p.code);
                    }}
                    className="btn-ghost text-[11px] px-2 py-1"
                    style={{ border: "1px solid var(--border-stone)", background: "#fff" }}
                    data-testid={`perk-copy-${p.kind}`}
                  >
                    Copy code
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
